import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Videocam,
  Info
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { liveCameraService } from '../../services/liveCameraService';

const DJLiveCamera = () => {
  // Buscar informações da câmera ao vivo
  const { data: liveCameraData, isLoading: liveCameraLoading } = useQuery({
    queryKey: ['liveCamera-active'],
    queryFn: () => liveCameraService.getActiveLiveCameraInfo(),
  });

  const liveCamera = liveCameraData?.data?.liveCamera;

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <Videocam sx={{ mr: 2, color: '#FF6B35' }} />
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Câmera ao Vivo
            </Typography>
          </Box>

          {liveCameraLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress sx={{ color: '#FF6B35' }} />
            </Box>
          ) : liveCamera ? (
            <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Instruções:</strong> Use estas informações para configurar o OBS Studio para transmissão de vídeo ao vivo.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(255, 107, 53, 0.05)', border: '1px solid rgba(255, 107, 53, 0.2)' }}>
                    <Typography variant="h6" sx={{ color: '#FF6B35', mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Info sx={{ mr: 1 }} />
                      Configurações RTMP
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        RTMP URL
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1, wordBreak: 'break-all' }}>
                        {liveCamera.rtmp_url}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        Stream Key
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {liveCamera.stream_key}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        Resolução
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {liveCamera.resolution}
                      </Typography>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        Bitrate
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {liveCamera.bitrate} kbps
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.7, mb: 0.5 }}>
                        FPS
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', bgcolor: 'rgba(0, 0, 0, 0.3)', p: 1, borderRadius: 1 }}>
                        {liveCamera.fps} fps
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.2)' }}>
                    <Typography variant="h6" sx={{ color: '#00D4FF', mb: 2, display: 'flex', alignItems: 'center' }}>
                      <Videocam sx={{ mr: 1 }} />
                      Visualizar Transmissão
                    </Typography>
                    
                    {liveCamera.stream_url ? (
                      <Box>
                        <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                          URL da Transmissão:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            bgcolor: 'rgba(0, 0, 0, 0.3)', 
                            p: 1, 
                            borderRadius: 1,
                            wordBreak: 'break-all',
                            mb: 2
                          }}
                        >
                          {liveCamera.stream_url}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigator.clipboard.writeText(liveCamera.stream_url)}
                          sx={{
                            borderColor: '#00D4FF',
                            color: '#00D4FF',
                            '&:hover': {
                              borderColor: '#00D4FF',
                              bgcolor: 'rgba(0, 212, 255, 0.1)'
                            }
                          }}
                        >
                          Copiar URL
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.7, fontStyle: 'italic' }}>
                        Nenhuma URL de visualização configurada
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <Alert severity="success" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Dica:</strong> No OBS Studio, vá em Configurações → Transmissão e use o tipo "Personalizado". Cole a RTMP URL no campo "Servidor" e a Stream Key no campo "Chave de transmissão".
                </Typography>
              </Alert>
            </Box>
          ) : (
            <Box textAlign="center" py={4}>
              <Videocam sx={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
              <Typography variant="h6" sx={{ opacity: 0.7 }}>
                Informações da câmera ao vivo não disponíveis
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Entre em contato com o administrador
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default DJLiveCamera;
